import { type ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import type { AppMenuItem, AppMenuConfig } from '@workspace/ui';
import { UserProfileMenu, buildMenuLookup, resolveMenuItemsFromLookup } from '@workspace/ui';
import {
  ContentFrame,
  DashboardShell,
  SidebarNav,
  TopBar,
  type DashboardShellAction,
  type DashboardShellFooterAction,
  type DashboardShellNavItem,
  type DashboardShellNavSection
} from '@workspace/silk-ui';
import { useTourAnchor } from '@workspace/tour';
import { useScope } from '@workspace/scope-context';
import {
  ExternalLink,
  Menu,
  Search,
  Star,
  StarOff,
  Shield,
  Users,
  Lock,
  Building,
  Zap,
  CreditCard,
  Activity,
  Server,
  Key,
  Map,
  Compass,
  Database,
  Palette,
  Image,
  Type,
  ClipboardList,
  ListChecks,
  ShoppingCart,
  Package,
  Tag,
  Settings,
  type LucideIcon
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import CommandPalette from '@/components/command/CommandPalette';
import { menuConfig, type MenuItem } from '@/config/navigation';
import { useNavigation } from '@/context/NavigationContext';
import { usePreferences } from '@/context/PreferencesContext';
import { useSession } from '@/context/SessionContext';
import { resolvePortalUrl } from '@/lib/publicUrls';
import { cn } from '@/lib/utils';
import { filterMenuConfig } from '@/lib/menu/filterMenu';

// ─── Nav recents (localStorage) ────────────────────────────────────────────

const NAV_RECENTS_KEY = 'dashboard.nav.recents';
const NAV_RECENTS_MAX = 8;

function readStoredRecents(): string[] {
  try {
    const raw = localStorage.getItem(NAV_RECENTS_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
}

function useNavRecents(fullMenuLookup: Map<string, AppMenuItem>) {
  const [recentIds, setRecentIds] = useState<string[]>(() =>
    typeof window !== 'undefined' ? readStoredRecents() : []
  );

  const recordRecent = useCallback((id: string) => {
    setRecentIds(prev => {
      const next = [id, ...prev.filter(r => r !== id)].slice(0, NAV_RECENTS_MAX);
      try { localStorage.setItem(NAV_RECENTS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const recents = useMemo(
    () => recentIds.flatMap(id => {
      const item = fullMenuLookup.get(id);
      return item ? [item] : [];
    }),
    [recentIds, fullMenuLookup]
  );

  return { recents, recordRecent };
}

// ─── Mobile bottom navigation ───────────────────────────────────────────────

type BottomNavItem = { id: string; label: string; route: string; Icon: LucideIcon };

function buildBottomNavItems(sideMenu: MenuItem[]): BottomNavItem[] {
  const ICON_MAP: Record<string, LucideIcon> = {
    shield: Shield, users: Users, building: Building, activity: Activity,
    server: Server, map: Map, settings: Settings, key: Key,
  };

  const candidates: BottomNavItem[] = [];

  for (const item of sideMenu) {
    if (item.id === 'settings') continue; // pinned separately at end
    const route = item.route ?? item.children?.[0]?.route;
    const icon = item.icon ? ICON_MAP[item.icon] : undefined;
    if (route && icon) candidates.push({ id: item.id, label: item.label, route, Icon: icon });
  }

  // Always include Settings at the end
  const settingsItem = sideMenu.find(m => m.id === 'settings');
  if (settingsItem?.route) candidates.push({ id: 'settings', label: 'Settings', route: settingsItem.route, Icon: Settings });

  return candidates.slice(0, 5);
}

function MobileBottomNav({
  items,
  currentPath,
  onNavigate,
}: {
  items: BottomNavItem[];
  currentPath: string;
  onNavigate: (route: string) => void;
}) {
  if (items.length === 0) return null;
  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-50 flex md:hidden border-t border-border bg-card shadow-[0_-1px_12px_rgb(0,0,0,0.08)]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      aria-label="Mobile navigation"
    >
      {items.map(({ id, label, route, Icon }) => {
        const isActive = currentPath === route || currentPath.startsWith(`${route}/`);
        return (
          <button
            key={id}
            type="button"
            onClick={() => onNavigate(route)}
            aria-label={label}
            aria-current={isActive ? 'page' : undefined}
            className={cn(
              'flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-semibold transition-colors min-h-[3rem]',
              isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <Icon className={cn('h-5 w-5 transition-transform', isActive && 'scale-110')} aria-hidden />
            <span className="truncate max-w-[3.5rem]">{label}</span>
          </button>
        );
      })}
    </nav>
  );
}

// ───────────────────────────────────────────────────────────────────────────

interface AppLayoutProps {
  path: string;
  children: ReactNode;
  footer?: ReactNode;
  contentClassName?: string;
  contentWrapperClassName?: string;
}

const QUICK_SEARCH_COMMAND_ID = 'search';
const QUICK_PORTAL_COMMAND_ID = 'open-portal';

export default function AppLayout({
  path,
  children,
  footer,
  contentClassName,
  contentWrapperClassName
}: AppLayoutProps) {
  const navigation = useNavigation();
  const { session, logout } = useSession();
  const { navigation: navigationPrefs, updateNavigation } = usePreferences();
  const { mode, activeTenant } = useScope();
  const currentPath = path || navigation.path;

  const brandTitle = 'ACD';
  const brandSubtitle = 'Platform Dashboard';
  const brandAnchor = useTourAnchor<HTMLDivElement>('dashboard.brand');
  const commandAnchor = useTourAnchor<HTMLDivElement>('dashboard.command');
  const contentAnchor = useTourAnchor<HTMLDivElement>('dashboard.workspace');

  const hasScopes = (session?.scopes?.length ?? 0) > 0;
  const hasTenantScope = mode === 'tenant' && Boolean(activeTenant);
  const grantedScopes = useMemo(() => collectGrantedScopes(session), [session]);

  const filteredMenuConfig = useMemo(
    () => filterMenuConfigByScopes(filterMenuConfig(menuConfig, hasTenantScope), grantedScopes),
    [grantedScopes, hasTenantScope]
  );

  const menu = useMemo(() => {
    if (hasScopes) {
      return filteredMenuConfig;
    }

    const fallbackIds = new Set(['dashboard', 'settings']);
    const sideMenu = (filteredMenuConfig.sideMenu ?? []).filter(item => fallbackIds.has(item.id));
    const userMenu = (filteredMenuConfig.userMenu ?? []).filter(item => fallbackIds.has(item.id));

    return {
      ...filteredMenuConfig,
      sideMenu,
      topMenu: [],
      userMenu
    };
  }, [filteredMenuConfig, hasScopes]);

  const menuLookup = useMemo(() => buildMenuLookup(menu), [menu]);
  const fullMenuLookup = useMemo(() => buildMenuLookup(menuConfig), []);

  const favoriteItems = useMemo(() => {
    const items = resolveMenuItemsFromLookup(fullMenuLookup, navigationPrefs.favorites.filter(id => menuLookup.has(id)));
    if (navigationPrefs.favoritesOrder === 'alpha') {
      return [...items].sort((a, b) => (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' }));
    }
    return items;
  }, [fullMenuLookup, navigationPrefs.favorites, navigationPrefs.favoritesOrder, menuLookup]);

  const sidebarSections = useMemo(
    () => buildSidebarSections(menu.sideMenu ?? [], favoriteItems, currentPath),
    [favoriteItems, menu.sideMenu, currentPath]
  );
  const sidebarFooterItems = useMemo(
    () => buildSidebarFooterItems(menu.sideMenu ?? [], currentPath),
    [menu.sideMenu, currentPath]
  );

  const [commandOpen, setCommandOpen] = useState(false);
  const [desktopSearchValue, setDesktopSearchValue] = useState('');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Recents tracking
  const { recents, recordRecent } = useNavRecents(fullMenuLookup);

  // Bottom nav items derived from filtered side menu
  const bottomNavItems = useMemo(
    () => buildBottomNavItems(menu.sideMenu ?? []),
    [menu.sideMenu]
  );

  // Swipe-to-close for mobile nav sheet
  const swipeTouchStartX = useRef<number>(0);
  const handleSheetTouchStart = useCallback((e: React.TouchEvent) => {
    swipeTouchStartX.current = e.touches[0].clientX;
  }, []);
  const handleSheetTouchEnd = useCallback((e: React.TouchEvent) => {
    const dx = e.changedTouches[0].clientX - swipeTouchStartX.current;
    if (dx < -56) setMobileNavOpen(false);
  }, []);

  const handleCommandOpenChange = useCallback((next: boolean) => {
    setCommandOpen(next);
  }, []);

  const handleOpenPortal = useCallback(() => {
    if (typeof window === 'undefined') return;
    const portalUrl = resolvePortalUrl(window.location.href, import.meta.env?.VITE_PORTAL_URL as string | undefined);
    if (!portalUrl) return;
    window.open(portalUrl, '_blank', 'noopener,noreferrer');
  }, []);

  const handleMenuNavigateById = useCallback(
    (itemId: string) => {
      const item = menuLookup.get(itemId) ?? fullMenuLookup.get(itemId);
      if (!item?.route) return;
      navigation.navigate(item.route);
      recordRecent(itemId);
      setMobileNavOpen(false);
    },
    [fullMenuLookup, menuLookup, navigation, recordRecent]
  );

  const topBarActions = useMemo<DashboardShellAction[]>(
    () => [
      {
        id: QUICK_SEARCH_COMMAND_ID,
        label: 'Search',
        icon: <Search className="h-4 w-4" aria-hidden />
      },
      {
        id: QUICK_PORTAL_COMMAND_ID,
        label: 'Open Portal',
        icon: <ExternalLink className="h-4 w-4" aria-hidden />
      }
    ],
    []
  );

  const handleTopBarAction = useCallback(
    (actionId: string) => {
      if (actionId === QUICK_SEARCH_COMMAND_ID) {
        handleCommandOpenChange(true);
        return;
      }
      if (actionId === QUICK_PORTAL_COMMAND_ID) {
        handleOpenPortal();
        return;
      }
    },
    [handleCommandOpenChange, handleOpenPortal]
  );

  const desktopSearchTrigger = (
    <div
      ref={commandAnchor}
      className={cn(
        'w-full max-w-xl transition-opacity duration-150',
        commandOpen ? 'pointer-events-none opacity-0' : 'opacity-100'
      )}
    >
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          aria-label="Search"
          value={desktopSearchValue}
          onChange={event => setDesktopSearchValue(event.target.value)}
          onFocus={() => handleCommandOpenChange(true)}
          placeholder="Search or type /"
          className="h-10 border-[color:var(--input-border)] bg-[color:var(--input-bg)] pl-9 pr-12 text-sm text-[color:var(--input-text)] placeholder:text-[color:var(--input-placeholder)] shadow-sm focus-visible:ring-[color:var(--input-border-focus)]"
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
          ⌘K
        </span>
      </div>
    </div>
  );

  const brand = useMemo(
    () => ({
      title: brandTitle,
      subtitle: brandSubtitle,
      monogram: 'ACD',
      hrefLabel: 'Dashboard home',
    }),
    [brandSubtitle, brandTitle]
  );

  const avatarLabel = useMemo(() => buildInitials(session?.username ?? session?.email ?? ''), [session?.email, session?.username]);

  return (
    <DashboardShell
      sidebar={
        <div ref={brandAnchor} className="hidden h-full md:block">
          <SidebarNav
            brand={brand}
            sections={sidebarSections}
            footerItems={sidebarFooterItems}
            compact={navigationPrefs.collapsed}
            onToggleCompact={() => updateNavigation({ collapsed: !navigationPrefs.collapsed })}
            onItemSelect={handleMenuNavigateById}
          />
        </div>
      }
      topBar={
        <TopBar
          title={brandTitle}
          contextLabel={session?.username ?? 'Workspace'}
          leadingSlot={
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="shrink-0 border-[var(--border)] bg-[var(--background)] shadow-sm md:hidden"
              aria-label="Open navigation menu"
              title="Open navigation menu"
              onClick={() => setMobileNavOpen(true)}
            >
              <Menu className="h-5 w-5" aria-hidden />
            </Button>
          }
          searchSlot={desktopSearchTrigger}
          actions={topBarActions}
          onAction={handleTopBarAction}
          profileSlot={
            <UserProfileMenu
              avatarLabel={avatarLabel}
              username={session?.username ?? undefined}
              email={session?.email ?? undefined}
              onLogout={logout}
              renderContent={(close: () => void) => (
                <div className="space-y-4">
                  <Button variant="secondary" className="w-full" onClick={() => { close(); handleOpenPortal(); }}>
                    Open Portal
                  </Button>
                </div>
              )}
            />
          }
        />
      }
    >
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" size="sm" className="w-64 max-w-[calc(100vw-1.5rem)] p-0 md:hidden">
          <SheetHeader className="sr-only">
            <SheetTitle>Main menu</SheetTitle>
          </SheetHeader>
          {/* Swipe-left to close */}
          <div
            className="h-full"
            onTouchStart={handleSheetTouchStart}
            onTouchEnd={handleSheetTouchEnd}
          >
            <SidebarNav
              brand={brand}
              sections={sidebarSections}
              footerItems={sidebarFooterItems}
              className="h-full w-full border-r-0"
              onItemSelect={handleMenuNavigateById}
            />
          </div>
        </SheetContent>
      </Sheet>
      <CommandPalette
        open={commandOpen}
        onOpenChange={handleCommandOpenChange}
        menu={menu}
        recents={recents}
        onNavigate={(next: string) => {
          navigation.navigate(next);
        }}
        onRecordRecent={recordRecent}
        initialQuery={desktopSearchValue}
        onQueryChange={setDesktopSearchValue}
      />
      <div ref={contentAnchor} data-app-content className="relative flex h-full min-h-0 min-w-0 flex-col">
        <ContentFrame
          className="min-h-0 flex-1"
          layout="canvas"
          padded={false}
          bodyClassName={cn('h-full min-h-0', contentClassName)}
        >
          <div className={contentWrapperClassName ?? 'h-full min-h-0'}>
            {children}
            {footer ? <div className="mt-6">{footer}</div> : null}
          </div>
        </ContentFrame>
      </div>
      <MobileBottomNav
        items={bottomNavItems}
        currentPath={currentPath}
        onNavigate={route => {
          navigation.navigate(route);
        }}
      />
    </DashboardShell>
  );
}

function buildInitials(source: string): string {
  if (!source) {
    return 'U';
  }
  const parts = source.trim().split(/\s+/);
  const first = parts[0]?.charAt(0) ?? '';
  const second = parts[1]?.charAt(0) ?? (parts[0]?.charAt(1) ?? '');
  const initials = `${first}${second}`.toUpperCase();
  return initials || 'U';
}

function renderMenuIcon(icon?: string): ReactNode {
  if (!icon) return null;
  
  const iconProps = { className: "h-4 w-4" };

  switch (icon) {
    case 'shield': return <Shield {...iconProps} />;
    case 'users': return <Users {...iconProps} />;
    case 'lock': return <Lock {...iconProps} />;
    case 'building': return <Building {...iconProps} />;
    case 'zap': return <Zap {...iconProps} />;
    case 'credit-card': return <CreditCard {...iconProps} />;
    case 'activity': return <Activity {...iconProps} />;
    case 'server': return <Server {...iconProps} />;
    case 'key': return <Key {...iconProps} />;
    case 'map': return <Map {...iconProps} />;
    case 'compass': return <Compass {...iconProps} />;
    case 'database': return <Database {...iconProps} />;
    case 'palette': return <Palette {...iconProps} />;
    case 'image': return <Image {...iconProps} />;
    case 'type': return <Type {...iconProps} />;
    case 'clipboard-list': return <ClipboardList {...iconProps} />;
    case 'list-checks': return <ListChecks {...iconProps} />;
    case 'shopping-cart': return <ShoppingCart {...iconProps} />;
    case 'package': return <Package {...iconProps} />;
    case 'tag': return <Tag {...iconProps} />;
    case 'settings': return <Settings {...iconProps} />;
    default:
      return (
        <span className="material-icons text-[18px] leading-none" aria-hidden>
          {icon}
        </span>
      );
  }
}

function routeMatches(currentPath: string, route?: string): boolean {
  if (!route) {
    return false;
  }
  if (route === '/') {
    return currentPath === '/';
  }
  return currentPath === route || currentPath.startsWith(`${route}/`);
}

function toShellNavItem(item: AppMenuItem, currentPath: string): DashboardShellNavItem {
  const children = item.children?.map(child => toShellNavItem(child, currentPath));
  const childActive = children?.some(child => child.active);
  const hasChildren = Boolean(children?.length);

  return {
    id: item.id,
    label: item.label,
    icon: renderMenuIcon(item.icon),
    badge: null,
    hint: item.description ?? null,
    active: routeMatches(currentPath, item.route) || childActive,
    collapsible: hasChildren,
    defaultExpanded: childActive,
    children: hasChildren ? children : undefined
  };
}

function buildSidebarSections(
  sideMenu: MenuItem[],
  favoriteItems: AppMenuItem[],
  currentPath: string
): DashboardShellNavSection[] {
  const sections: DashboardShellNavSection[] = [];

  if (favoriteItems.length > 0) {
    sections.push({
      id: 'favorites',
      label: 'Favorites',
      items: favoriteItems.map(item => toShellNavItem(item, currentPath))
    });
  }

  const primaryItems: DashboardShellNavItem[] = [];

  sideMenu.forEach(item => {
    if (item.id === 'settings') {
      return;
    }
    if (item.children?.length) {
      primaryItems.push(toShellNavItem(item, currentPath));
      return;
    }
    if (item.route) {
      primaryItems.push(toShellNavItem(item, currentPath));
    }
  });

  if (primaryItems.length > 0) {
    sections.push({
      id: 'primary',
      label: 'Workspace',
      items: primaryItems
    });
  }

  return sections;
}

function buildSidebarFooterItems(
  sideMenu: MenuItem[],
  currentPath: string
): DashboardShellFooterAction[] {
  const settingsItem = sideMenu.find(item => item.id === 'settings' && item.route);
  if (!settingsItem?.route) {
    return [];
  }

  return [
    {
      id: settingsItem.id,
      label: settingsItem.label,
      icon: renderMenuIcon(settingsItem.icon),
      hint: settingsItem.description ?? null,
      active: routeMatches(currentPath, settingsItem.route)
    }
  ];
}

function collectGrantedScopes(session: { scopes?: string[]; permissionSummary?: Record<string, unknown> } | null | undefined): Set<string> {
  const granted = new Set(session?.scopes ?? []);
  const summary = session?.permissionSummary;
  if (!summary || typeof summary !== 'object') {
    return granted;
  }

  Object.values(summary).forEach(value => {
    if (Array.isArray(value)) {
      value.forEach(entry => {
        if (typeof entry === 'string') {
          granted.add(entry);
        }
      });
      return;
    }

    if (value && typeof value === 'object') {
      Object.values(value as Record<string, unknown>).forEach(inner => {
        if (!Array.isArray(inner)) {
          return;
        }
        inner.forEach(entry => {
          if (typeof entry === 'string') {
            granted.add(entry);
          }
        });
      });
    }
  });

  return granted;
}

function filterMenuConfigByScopes(config: AppMenuConfig, grantedScopes: Set<string>) {
  return {
    ...config,
    sideMenu: filterMenuItemsByScopes(config.sideMenu ?? [], grantedScopes),
    topMenu: filterMenuItemsByScopes(config.topMenu ?? [], grantedScopes),
    userMenu: filterMenuItemsByScopes(config.userMenu ?? [], grantedScopes)
  };
}

function filterMenuItemsByScopes(items: MenuItem[], grantedScopes: Set<string>): MenuItem[] {
  return items
    .filter(item => {
      const required = item.requiredScopes ?? [];
      return required.length === 0 || required.some(scope => grantedScopes.has(scope));
    })
    .map(item => ({
      ...item,
      children: item.children ? filterMenuItemsByScopes(item.children, grantedScopes) : undefined
    }))
    .filter(item => (item.children ? item.children.length > 0 || Boolean(item.route) : true));
}
